import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import { listAdminPendingMarketplaceProducts } from "../../../features/marketplace/adminMarketplaceApi";
import {
  CATEGORY_LABELS,
  PRODUCT_CATEGORIES,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import cls from "./AdminPendingProductsPage.module.scss";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "title_asc", label: "Title A-Z" },
  { value: "title_desc", label: "Title Z-A" },
];

const getClubLink = (club) => {
  if (!club?._id) return "#";
  if (!club.cityId || !club.streetId) return `/admin/clubs/single/${club._id}`;
  return `/admin/cities/single/${club.cityId}/streets/${club.streetId}/clubs/${club._id}`;
};

function ProductMediaCarousel({ product }) {
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const mediaItems = useMemo(() => {
    const images = (product?.listingImages || []).map((item) => ({
      id: `img-${item.key}`,
      type: "image",
      url: item.url,
    }));

    const videos = (product?.listingVideos || []).map((item) => ({
      id: `vid-${item.videoId}`,
      type: "video",
      videoId: item.videoId,
      url: item.url,
    }));

    return [...images, ...videos];
  }, [product]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [product?._id]);

  if (!mediaItems.length) {
    return (
      <div className={cls.emptyMedia}>
        <span>No media uploaded</span>
      </div>
    );
  }

  const current = mediaItems[index] || mediaItems[0];

  return (
    <div className={cls.mediaWrap}>
      <div className={cls.mediaSquare}>
        {current.type === "image" ? (
          <img
            src={current.url}
            alt={product?.title || "Product media"}
            className={cls.media}
          />
        ) : (
          <>
            {current.videoId ? (
              <VideoPlayer
                className={cls.videoPlayer}
                token={token}
                videoId={current.videoId}
                mode="proxy"
                controls
                muted={false}
              />
            ) : (
              <VideoPlayer
                className={cls.videoPlayer}
                hlsMasterUrl={current.url}
                controls
                muted={false}
              />
            )}
          </>
        )}

        {mediaItems.length > 1 ? (
          <>
            <button
              type="button"
              className={`${cls.navBtn} ${cls.prevBtn}`}
              onClick={() =>
                setIndex(
                  (prev) => (prev - 1 + mediaItems.length) % mediaItems.length,
                )
              }
              aria-label="Previous media"
            >
              ‹
            </button>

            <button
              type="button"
              className={`${cls.navBtn} ${cls.nextBtn}`}
              onClick={() => setIndex((prev) => (prev + 1) % mediaItems.length)}
              aria-label="Next media"
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      {mediaItems.length > 1 ? (
        <div className={cls.dots}>
          {mediaItems.map((item, dotIndex) => (
            <button
              key={item.id}
              type="button"
              className={`${cls.dot} ${dotIndex === index ? cls.dotActive : ""}`}
              onClick={() => setIndex(dotIndex)}
              aria-label={`Go to media ${dotIndex + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminPendingProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [subtype, setSubtype] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await listAdminPendingMarketplaceProducts({
          limit: 100,
          sort,
        });

        if (!mounted) return;
        setProducts(data?.products || []);
      } catch (err) {
        if (!mounted) return;
        setProducts([]);
        setError(
          err?.response?.data?.msg || "Could not load pending products.",
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [sort]);

  const subtypeOptions = useMemo(() => {
    const set = new Set();

    products.forEach((item) => {
      if (category !== "all" && item.category !== category) return;
      if (item.serviceType) set.add(item.serviceType);
      if (item.digitalType) set.add(item.digitalType);
    });

    return Array.from(set);
  }, [category, products]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();

    return products.filter((item) => {
      const productSubtype = item.serviceType || item.digitalType || "";
      const matchesSearch =
        !q ||
        item.title?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        item.club?.clubName?.toLowerCase().includes(q) ||
        item.seller?.username?.toLowerCase().includes(q);

      const matchesCategory = category === "all" || item.category === category;
      const matchesSubtype = subtype === "all" || productSubtype === subtype;

      return matchesSearch && matchesCategory && matchesSubtype;
    });
  }, [products, search, category, subtype]);

  return (
    <section className={cls.page}>
      <header className={cls.header}>
        <div>
          <p className={cls.kicker}>Marketplace Moderation</p>
          <h1 className={cls.title}>Pending Products</h1>
          <p className={cls.subtitle}>
            Review all products waiting for admin approval.
          </p>
        </div>

        <div className={cls.countBadge}>
          <span className={cls.countNumber}>{filteredProducts.length}</span>
          <span className={cls.countText}>visible</span>
        </div>
      </header>

      <section className={cls.filtersPanel}>
        <label className={cls.field}>
          <span className={cls.fieldLabel}>Search</span>
          <input
            className={cls.input}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search title, club, or username"
          />
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Category</span>
          <select
            className={cls.input}
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setSubtype("all");
            }}
          >
            <option value="all">All categories</option>
            {Object.values(PRODUCT_CATEGORIES).map((value) => (
              <option key={value} value={value}>
                {CATEGORY_LABELS[value] || value}
              </option>
            ))}
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Subtype</span>
          <select
            className={cls.input}
            value={subtype}
            onChange={(event) => setSubtype(event.target.value)}
          >
            <option value="all">All subtypes</option>
            {subtypeOptions.map((value) => (
              <option key={value} value={value}>
                {SUBTYPE_LABELS[value] || value}
              </option>
            ))}
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Sort</span>
          <select
            className={cls.input}
            value={sort}
            onChange={(event) => setSort(event.target.value)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      {loading ? (
        <div className={cls.state}>Loading pending products...</div>
      ) : null}

      {!loading && error ? <div className={cls.error}>{error}</div> : null}

      {!loading && !error && !filteredProducts.length ? (
        <div className={cls.state}>
          No pending products found for current filters.
        </div>
      ) : null}

      {!loading && !error && filteredProducts.length ? (
        <div className={cls.grid}>
          {filteredProducts.map((product) => {
            const avatar = product?.seller?.avatarUrl;
            const subtypeValue =
              product.serviceType || product.digitalType || "";

            return (
              <article key={product._id} className={cls.card}>
                <ProductMediaCarousel product={product} />

                <div className={cls.content}>
                  <div className={cls.chips}>
                    <span className={cls.categoryChip}>
                      {CATEGORY_LABELS[product.category] ||
                        product.category ||
                        "Product"}
                    </span>

                    {subtypeValue ? (
                      <span className={cls.subtypeChip}>
                        {SUBTYPE_LABELS[subtypeValue] || subtypeValue}
                      </span>
                    ) : null}
                  </div>

                  <h3 className={cls.productTitle}>{product.title}</h3>

                  {product.description ? (
                    <p className={cls.description}>{product.description}</p>
                  ) : (
                    <p className={cls.descriptionMuted}>
                      No description provided for this product.
                    </p>
                  )}

                  <div className={cls.metaLine}>
                    <span className={cls.metaLabel}>Club</span>
                    {product.club ? (
                      <Link
                        to={getClubLink(product.club)}
                        className={cls.metaLink}
                      >
                        {product.club.clubName || "Untitled club"}
                      </Link>
                    ) : (
                      <span className={cls.metaValue}>Unknown club</span>
                    )}
                  </div>

                  <div className={cls.sellerCard}>
                    <div className={cls.memberRow}>
                      <Link
                        to={`/admin/members/single/${product.seller?.userId}`}
                        className={cls.avatarLink}
                      >
                        {avatar ? (
                          <img
                            src={avatar}
                            alt={product.seller?.username || "seller"}
                            className={cls.avatar}
                          />
                        ) : (
                          <span className={cls.avatarFallback}>
                            {(product.seller?.username || "?")
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        )}
                      </Link>

                      <div className={cls.sellerMeta}>
                        <div className={cls.metaLabel}>Submitted by</div>
                        <Link
                          to={`/admin/members/single/${product.seller?.userId}`}
                          className={cls.metaLink}
                        >
                          @{product.seller?.username || "unknown"}
                        </Link>
                      </div>
                    </div>

                    <Link
                      to={`/admin/marketplace/pending-products/${product._id}`}
                      className={cls.viewProductButton}
                    >
                      <span>View Product</span>
                      <span className={cls.viewProductArrow}>→</span>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
